import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api, track } from "@/lib/api";
import CouponField from "./CouponField";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const INDIAN_CITIES = [
  "Bengaluru","Mumbai","Delhi","Chennai","Hyderabad","Kolkata","Pune","Ahmedabad","Surat","Jaipur",
  "Lucknow","Kanpur","Nagpur","Indore","Bhopal","Patna","Thiruvananthapuram","Kochi","Coimbatore",
  "Madurai","Varanasi","Ujjain","Amritsar","Ludhiana","Chandigarh","Guwahati","Bhubaneswar","Cuttack",
  "Nashik","Vadodara","Rajkot","Visakhapatnam","Vijayawada","Mysore","Mangaluru","Hubli","Belagavi"
];

export default function BirthForm() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    dob: "",
    time: "",
    ampm: "AM",
    unknown_time: false,
    birth_place: "",
    mobile: "",
    language: lang,
  });
  const [suggest, setSuggest] = useState([]);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const [coupon, setCoupon] = useState(null);

  const onChange = (k, v) => {
    if (!started) { track("form_start"); setStarted(true); }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onCityInput = (v) => {
    onChange("birth_place", v);
    if (v.length >= 2) {
      setSuggest(INDIAN_CITIES.filter((c) => c.toLowerCase().includes(v.toLowerCase())).slice(0, 6));
    } else setSuggest([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.mobile.replace(/\D/g, "").slice(-10))) {
      toast.error(t("form.err.mobile"));
      return;
    }
    if (!form.full_name || !form.dob || !form.birth_place) {
      toast.error(t("form.err.required"));
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const { data } = await api.post("/leads", {
        full_name: form.full_name,
        dob: form.dob,
        birth_time: form.unknown_time ? "12:00" : form.time || "12:00",
        ampm: form.ampm,
        unknown_time: form.unknown_time,
        birth_place: form.birth_place,
        mobile: form.mobile,
        coupon_code: coupon?.coupon?.code || null,
        language: lang,
        landing_language: lang,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        referrer: document.referrer || "",
      });
      navigate(`/${lang}/analysis/${data.lead_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("form.err.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="birth-form"
      className="glass-panel rounded-3xl p-6 sm:p-10 max-w-2xl mx-auto"
    >
      <h2 className="font-serif-lux text-2xl sm:text-3xl text-gold-hi mb-2">{t("form.title")}</h2>
      <p className="text-muted-warm text-sm mb-8">{t("form.subtitle")}</p>

      <div className="space-y-6">
        <Field label={t("form.name")}>
          <input
            data-testid="birth-form-full-name"
            type="text"
            value={form.full_name}
            onChange={(e) => onChange("full_name", e.target.value)}
            placeholder={t("form.name.ph")}
            className="input-warm"
            required
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("form.dob")}>
            <input
              data-testid="birth-form-dob"
              type="date"
              value={form.dob}
              onChange={(e) => onChange("dob", e.target.value)}
              className="input-warm"
              required
            />
          </Field>
          <Field label={t("form.time")}>
            <div className="flex gap-2">
              <input
                data-testid="birth-form-time"
                type="time"
                value={form.time}
                disabled={form.unknown_time}
                onChange={(e) => onChange("time", e.target.value)}
                className="input-warm flex-1"
              />
              <select
                data-testid="birth-form-ampm"
                value={form.ampm}
                onChange={(e) => onChange("ampm", e.target.value)}
                className="input-warm w-20"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </Field>
        </div>

        <label className="flex items-center gap-3 text-sm text-muted-warm cursor-pointer">
          <input
            data-testid="birth-form-unknown-time"
            type="checkbox"
            checked={form.unknown_time}
            onChange={(e) => onChange("unknown_time", e.target.checked)}
            className="accent-[#D6AA45] w-4 h-4"
          />
          {t("form.unknown")}
        </label>

        <Field label={t("form.place")}>
          <div className="relative">
            <input
              data-testid="birth-form-birth-city"
              type="text"
              value={form.birth_place}
              onChange={(e) => onCityInput(e.target.value)}
              placeholder={t("form.place.ph")}
              className="input-warm"
              required
              autoComplete="off"
            />
            {suggest.length > 0 && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card-elev border border-gold rounded-xl overflow-hidden max-h-60 overflow-y-auto">
                {suggest.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { onChange("birth_place", c); setSuggest([]); }}
                    className="w-full text-left px-4 py-2.5 text-ivory hover:bg-card-warm text-sm"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Field>

        <Field label={t("form.mobile")}>
          <div className="flex">
            <span className="input-warm input-prefix">+91</span>
            <input
              data-testid="birth-form-mobile"
              type="tel"
              inputMode="numeric"
              value={form.mobile}
              onChange={(e) => onChange("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder={t("form.mobile.ph")}
              className="input-warm input-suffix"
              required
              autoComplete="tel-national"
            />
          </div>
          <p className="text-[11px] text-muted-warm mt-1.5">{t("form.mobile.hint")}</p>
        </Field>

        <CouponField coupon={coupon} onChange={setCoupon} />

        <button
          data-testid="birth-form-submit-btn"
          type="submit"
          disabled={busy}
          className="btn-gold w-full text-sm disabled:opacity-60"
        >
          {busy ? "..." : t("form.submit")}
        </button>

        <p className="text-xs text-muted-warm text-center">{t("form.privacy")}</p>
      </div>

    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-gold mb-2">{label}</span>
      {children}
    </label>
  );
}
